/**
 * A magnifier for the price line: points near the cursor spread apart and grow,
 * points further away stay put.
 *
 * The cheap version of the Mac dock's magnification: one formula per point, no
 * animation loop — CSS transitions do the movement. Pure, so CRA's jest can
 * test it without a DOM.
 *
 * The formula is the classic "graphical fisheye": inside the lens, a point at
 * distance d from the cursor moves to (k+1)·d / (k·d/r + 1), which pushes near
 * points outward hard and far ones barely, and lands exactly on the lens edge
 * at d = r, so nothing jumps where the lens ends.
 *
 * Points that still land on top of each other — ten free plans at $0 have the
 * same position whatever the lens does — stack upward instead.
 */

export interface LensPoint {
  /** The plan's id: what the dot is. */
  id: number;
  /** Where it sits on the line, 0..1. */
  at: number;
}

export interface LensDot {
  id: number;
  /** Pixels from the left of the line. */
  x: number;
  /** How many dots this one sits on top of; 0 is on the line. */
  level: number;
  /** Size, as a multiple of the resting dot. */
  scale: number;
}

export interface LensOptions {
  /** How far either side of the cursor the lens reaches, in pixels. */
  radius?: number;
  /** How hard it magnifies. 0 is no lens. */
  strength?: number;
  /** The resting dot's diameter in pixels. */
  dot?: number;
  /** The most a dot grows, at the cursor. */
  grow?: number;
  /** The highest a stack goes; beyond this dots share the top. */
  maxLevel?: number;
}

/** How far either side of its centre the lens reaches, in pixels. */
export const LENS_RADIUS = 90;

const DEFAULTS: Required<LensOptions> = {
  radius: LENS_RADIUS,
  strength: 4,
  dot: 8,
  grow: 1.6,
  maxLevel: 12,
};

/**
 * Where each dot goes, given the cursor (pixels from the left, or null when the
 * pointer is not over the line) and the line's width in pixels.
 */
export function lensLayout(
  points: LensPoint[],
  cursor: number | null,
  width: number,
  options: LensOptions = {},
): LensDot[] {
  const o = { ...DEFAULTS, ...options };
  const placed = points.map(p => {
    const x = Math.max(0, Math.min(1, p.at)) * width;
    if (cursor === null) return { id: p.id, x, scale: 1 };
    const d = x - cursor;
    const near = Math.abs(d);
    if (near >= o.radius) return { id: p.id, x, scale: 1 };
    const t = near / o.radius;
    const pushed = ((o.strength + 1) * t) / (o.strength * t + 1);
    return {
      id: p.id,
      // Kept on the line: a dot pushed past either end would sit off the card.
      x: Math.max(0, Math.min(width, cursor + Math.sign(d) * pushed * o.radius)),
      scale: 1 + (o.grow - 1) * (1 - t),
    };
  });

  // STACK WHAT STILL OVERLAPS. Only under the lens: at rest the line stays a
  // line, which is what makes it readable at a glance.
  const order = placed.map((p, i) => i).sort((a, b) => placed[a].x - placed[b].x);
  const levels = new Array<number>(placed.length).fill(0);
  if (cursor !== null) {
    let lastX = -Infinity;
    let lastSize = 0;
    let level = 0;
    for (const i of order) {
      const p = placed[i];
      const size = o.dot * p.scale;
      const inLens = Math.abs(p.x - cursor) < o.radius;
      if (inLens && p.x - lastX < (size + lastSize) / 2) {
        level = Math.min(level + 1, o.maxLevel);
      } else {
        level = 0;
        lastX = p.x;
        lastSize = size;
      }
      levels[i] = level;
    }
  }
  return placed.map((p, i) => ({ ...p, level: levels[i] }));
}

/** How far a stacked dot sits above the one below it, as a multiple of the
 *  dot. More than the most a dot grows under the lens (1.6), or a grown dot
 *  covers the one above it. */
export const STACK_STEP = 1.8;

/** The most the line zooms in when you scroll over it. */
export const MAX_ZOOM = 12;

/**
 * The lens at a zoom level: scrolling makes it magnify harder and reach a
 * little wider. At 1 it is the plain lens.
 */
export function lensAt(zoom: number): { radius: number; strength: number } {
  const z = Math.max(1, Math.min(MAX_ZOOM, zoom));
  return { radius: LENS_RADIUS * (1 + (z - 1) * 0.15), strength: DEFAULTS.strength * z };
}

export interface Nudge {
  dx: number;
  dy: number;
}

/**
 * MAKE ROOM AROUND THE DOT YOU ARE ON. Dots close to the hovered one move a
 * few pixels straight away from it, the closest the most, so the one you are
 * pointing at stands clear. When the pointer leaves it, nothing is hovered
 * and they all go back, so the next one can be pointed at.
 *
 * `dy` is upward, like the stacks. A dot right on top of the hovered one has no
 * direction to go, so it goes up.
 */
export function nudgeAway(
  dots: LensDot[],
  hovered: number | null,
  { dot = DEFAULTS.dot, reach = 30, most = 10 }: { dot?: number; reach?: number; most?: number } = {},
): Map<number, Nudge> {
  const out = new Map<number, Nudge>();
  const on = hovered === null ? undefined : dots.find(d => d.id === hovered);
  if (!on) return out;
  const y = (d: LensDot) => d.level * dot * STACK_STEP;
  for (const d of dots) {
    if (d.id === on.id) continue;
    const vx = d.x - on.x;
    const vy = y(d) - y(on);
    const far = Math.hypot(vx, vy);
    if (far >= reach) continue;
    const push = most * (1 - far / reach);
    out.set(d.id, far === 0 ? { dx: 0, dy: push } : { dx: (vx / far) * push, dy: (vy / far) * push });
  }
  return out;
}
